import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scan, CheckCircle2, AlertTriangle, ArrowLeft, RotateCcw, Coins } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import { api, formatApiError } from "@/lib/api";

const STATE = {
  IDLE: "idle",
  LOOKED_UP: "lookedup",
  REDEEMED: "redeemed",
};

const STATUS_PILL_CLASSES = {
  approved: "border-[#16A34A] bg-[#16A34A] text-white",
  rejected: "border-[#DC2626] bg-[#FEE2E2] text-[#DC2626]",
};
const REDEEMED_PILL_CLASSES = "bg-[#09090B] text-white border-[#09090B]";
const DEFAULT_PILL_CLASSES = "border-[#09090B] bg-[#FFCC00] text-[#09090B]";

function pillClassesFor(state, status) {
  if (state === STATE.REDEEMED) return REDEEMED_PILL_CLASSES;
  return STATUS_PILL_CLASSES[status] || DEFAULT_PILL_CLASSES;
}

function pillLabelFor(state, status) {
  if (state === STATE.REDEEMED) return "Redeemed";
  return status;
}

export default function AdminRedeem() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [state, setState] = useState(STATE.IDLE);
  const [submission, setSubmission] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const norm = (s) => s.toUpperCase().replace(/\s+/g, "").trim();

  const reset = () => {
    setCode("");
    setSubmission(null);
    setError("");
    setState(STATE.IDLE);
  };

  const onLookup = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/tokens/${encodeURIComponent(norm(code))}`);
      setSubmission(data);
      setState(data.redeemed_at ? STATE.REDEEMED : STATE.LOOKED_UP);
    } catch (err) {
      setSubmission(null);
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setState(STATE.IDLE);
    } finally {
      setBusy(false);
    }
  };

  const onRedeem = async () => {
    if (!submission) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/admin/tokens/${encodeURIComponent(submission.token_code)}/redeem`);
      setSubmission(data);
      setState(STATE.REDEEMED);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white min-h-screen text-[#09090B]">
      <SiteHeader />

      <section className="border-b-2 border-[#09090B] bg-[#FFCC00]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="ws-label">/ Company Store</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 leading-[0.95]">
              REDEEM SAFETY TOKEN
            </h1>
            <p className="mt-2 text-[#09090B] max-w-lg text-sm">
              Enter the code on the employee&apos;s ticket. Each token is single-use.
            </p>
          </div>
          <Link
            to="/admin"
            data-testid="back-to-admin-link"
            className="inline-flex items-center gap-2 bg-[#09090B] text-white ws-label px-4 py-2 border-2 border-[#09090B] hover:bg-[#FF5A00] transition-colors"
          >
            <ArrowLeft strokeWidth={2.5} className="w-4 h-4" /> Back to admin
          </Link>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-12 py-10 space-y-8">
        {/* Code entry */}
        <form
          onSubmit={onLookup}
          className="border-2 border-[#09090B] bg-white p-6 ws-hard-shadow"
          data-testid="redeem-form"
        >
          <label htmlFor="code" className="ws-label flex items-center gap-2">
            <Scan strokeWidth={2.5} className="w-4 h-4" /> Token code
          </label>
          <div className="mt-3 grid grid-cols-[1fr_auto] gap-3">
            <input
              id="code"
              data-testid="token-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="SAFE-XXXX-XXXX"
              className="w-full border-2 border-[#09090B] bg-white px-4 py-3 font-mono font-bold text-lg uppercase tracking-widest focus:border-[#FF5A00] outline-none"
              autoFocus
            />
            <button
              type="submit"
              disabled={busy || !code.trim()}
              data-testid="lookup-btn"
              className="bg-[#FF5A00] hover:bg-[#E65100] text-white ws-label px-6 py-3 border-2 border-[#09090B] ws-hard-shadow-sm disabled:opacity-60"
            >
              {busy ? "…" : "Look up"}
            </button>
          </div>
          {error && (
            <div
              data-testid="redeem-error"
              className="mt-4 border-2 border-[#DC2626] bg-[#FEE2E2] text-[#DC2626] px-4 py-3 ws-label flex items-start gap-2"
            >
              <AlertTriangle strokeWidth={2.5} className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}
        </form>

        {/* Submission card */}
        {submission && (
          <div
            className="border-2 border-[#09090B] bg-white ws-hard-shadow overflow-hidden grid md:grid-cols-[260px_1fr]"
            data-testid="redeem-card"
          >
            <div className="bg-[#F4F4F5] border-r-2 border-[#09090B] aspect-square md:aspect-auto">
              <img
                src={submission.photo_url}
                alt={submission.employee_name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 flex-wrap">
                <span
                  className={`ws-label px-3 py-1 border-2 ${pillClassesFor(state, submission.status)}`}
                  data-testid="redeem-status"
                >
                  {pillLabelFor(state, submission.status)}
                </span>
                <span className="font-display font-bold text-2xl tracking-tight">
                  {submission.employee_name}
                </span>
                <span className="text-sm text-[#52525B]">ID · {submission.employee_id}</span>
              </div>
              <p className="mt-3 text-[#09090B] leading-relaxed">{submission.reason}</p>

              <div className="mt-5 flex items-center gap-3 bg-[#F4F4F5] border-2 border-[#09090B] px-4 py-2 inline-flex">
                <Coins strokeWidth={2.5} className="w-4 h-4 text-[#FF5A00]" />
                <span className="ws-label text-[#52525B]">Token</span>
                <span className="font-mono font-bold text-[#09090B]" data-testid="lookup-token-code">
                  {submission.token_code}
                </span>
              </div>

              {state === STATE.REDEEMED ? (
                <div className="mt-6 border-2 border-[#16A34A] bg-[#F0FDF4] px-4 py-3 flex items-start gap-3" data-testid="redeem-success">
                  <CheckCircle2 strokeWidth={2.5} className="w-5 h-5 text-[#16A34A] mt-0.5" />
                  <div>
                    <div className="ws-label text-[#16A34A]">Redeemed</div>
                    <div className="text-sm text-[#09090B] mt-1">
                      {submission.redeemed_at && new Date(submission.redeemed_at).toLocaleString()}
                      {submission.redeemed_by && <> · by {submission.redeemed_by}</>}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-6 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={onRedeem}
                    disabled={busy || submission.status !== "approved"}
                    data-testid="confirm-redeem-btn"
                    className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-[#15803D] text-white font-bold px-6 py-3 ws-label border-2 border-[#09090B] ws-hard-shadow-sm disabled:opacity-60"
                  >
                    <CheckCircle2 strokeWidth={2.5} className="w-4 h-4" />
                    {busy ? "Redeeming…" : "Mark as Redeemed"}
                  </button>
                  {submission.status !== "approved" && (
                    <div className="text-sm text-[#52525B]">
                      This submission must be approved before redemption.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reset */}
        {(submission || error) && (
          <button
            type="button"
            onClick={reset}
            data-testid="reset-btn"
            className="inline-flex items-center gap-2 ws-label text-[#09090B] hover:text-[#FF5A00] transition-colors"
          >
            <RotateCcw strokeWidth={2.5} className="w-4 h-4" /> Look up another token
          </button>
        )}
      </section>
    </div>
  );
}
